import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import mixpanel, { initMixpanel } from '../utils/mixpanelConsent';
import Helmet from 'react-helmet';
import Compressor from 'compressorjs';

import AuthService from './AuthService';
import withAuth from './WithAuth';
import CardIdentify from './Post/CardIdentify';
import { GameContext } from '../contexts/GameContext';
import { buildConditionsList, LEGACY_CONDITIONS, LEGACY_NO_FOIL_RARITIES } from '../utils/conditions';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';

initMixpanel();
var SHARE_MIN_PRICE = constants.SHARE_MIN_PRICE || 10;

const Auth = new AuthService();

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

class Post extends Component {
  static contextType = GameContext;

  constructor() {
    super();
    this.state = {
      step: 1,
      selectedCard: null,
      cardImage: null,
      title: '',
      description: '',
      price: '',
      quantity: '1',
      condition: 'NM',
      category: 'singles',
      photos: [],
      photoError: '',
      processingPhoto: false,
      allowSharing: false,
      shareCommission: 1,
      acceptOffers: false,
      shareCommissionType: 'amount',
      showCardDetails: false,
      showPhotos: false,
      hasPayoutMethod: null,
      loadingIcon: 'none',
      errors: {},
      pricingData: null,
      pricingLoading: false,
      showSuccessModal: false,
      createdPostId: null,
      priceTouched: false,
      titleTouched: false,
      // Sealed product fields
      productType: 'single',
      selectedSealedProduct: null,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    var locationState = this.props.location && this.props.location.state;
    if (locationState && locationState.prefillCard) {
      this.onCardSelected(locationState.prefillCard);
    }
    // Theme listener
    this._handleThemeChanged = function () { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
    // Check payout method
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/users/' + this.props.user._id, { mode: 'cors', method: 'GET' })
      .then(function (res) {
        var hasPayout = !!(res.profile.stripeAccountId || res.profile.paypalEmail);
        self.setState({ hasPayoutMethod: hasPayout });
      })
      .catch(function () {
        self.setState({ hasPayoutMethod: false });
      });
  }

  componentDidUpdate() {
    document.body.style.overflow = this.state.showSuccessModal ? 'hidden' : '';
  }

  componentWillUnmount() {
    window.removeEventListener('themeChanged', this._handleThemeChanged);
    document.body.style.overflow = '';
  }

  onCardSelected(card) {
    var cardImg = resolveCardImage(card.image);

    this.setState({
      selectedCard: card,
      cardImage: cardImg,
      title: card.name + (card.variant ? ' (' + card.variant + ')' : ''),
      description: [card.cardSet, card.rarity].filter(Boolean).join(' - '),
      category: 'singles',
      condition: 'NM',
      step: 2,
    });

    this.fetchPricing(card._id);
  }

  onSealedCatalogSelected(card) {
    var cardImg = card.image ? (card.image.indexOf('http') === 0 ? card.image : constants.DOMAIN + '/' + card.image) : null;
    this.setState({
      step: 2,
      selectedSealedProduct: card,
      selectedCard: null,
      cardImage: cardImg,
      title: card.name,
      description: '',
      category: 'sealed',
      condition: 'NM',
      pricingData: null,
    });
  }

  fetchPricing(cardId) {
    var self = this;
    this.setState({ pricingLoading: true });
    fetch(constants.DOMAIN + '/api/v1/guest/card/' + cardId + '/pricing', { mode: 'cors' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        self.setState({ pricingData: data.pricing || null, pricingLoading: false });
      })
      .catch(function () {
        self.setState({ pricingData: null, pricingLoading: false });
      });
  }

  renderPricingWidget() {
    var pricingData = this.state.pricingData;
    var condition = this.state.condition;
    if (!pricingData || Object.keys(pricingData).length === 0) return null;
    var isDark = document.documentElement.classList.contains('tw-dark');

    var condLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === condition; });
    var condName = condLabel ? condLabel.label : condition;
    var data = pricingData[condition];
    var fallback = false;
    var fallbackCondName = '';

    if (!data) {
      // Find fallback: condition with most data points
      var bestCond = null;
      var bestCount = 0;
      Object.keys(pricingData).forEach(function (key) {
        if (pricingData[key].count > bestCount) {
          bestCount = pricingData[key].count;
          bestCond = key;
        }
      });
      if (!bestCond) return null;
      data = pricingData[bestCond];
      fallback = true;
      var fbLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === bestCond; });
      fallbackCondName = fbLabel ? fbLabel.label : bestCond;
    }

    var sourceText = data.count + ' ' + (data.source === 'sales' ? (data.count === 1 ? 'sale' : 'sales') : (data.count === 1 ? 'listing' : 'listings'));

    return (
      <div
        className="tw-mb-4 tw-rounded-xl tw-p-4"
        style={{ backgroundColor: isDark ? '#1a1a1a' : '#f9fafb', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
      >
        <div>
          {fallback ? (
            <div className="tw-mb-2">
              <p className="tw-text-xs tw-text-[#888] tw-mb-1 tw-mt-0">
                No {condName} data
              </p>
              <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-0" style={{ color: isDark ? '#ccc' : '#555' }}>
                {fallbackCondName} prices ({sourceText}):
              </p>
            </div>
          ) : (
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-0" style={{ color: isDark ? '#ccc' : '#555' }}>
                {condName} Prices
              </p>
              <p className="tw-text-[10px] tw-text-[#999] tw-mb-0 tw-mt-0">
                Based on {sourceText}
              </p>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-text-center">
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Low</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.low.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">Median</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.median.toFixed(2)}</p>
            </div>
            <div className="tw-flex-1">
              <p className="tw-text-[10px] tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-0.5 tw-mt-0">High</p>
              <p className="tw-text-sm tw-font-bold tw-mb-0 tw-mt-0" style={{ color: '#c9a84c' }}>${data.high.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  goToReview() {
    var errors = {};
    if (!this.state.title.trim()) errors.title = 'Title is required';
    if (!this.state.price || isNaN(Number(this.state.price)) || Number(this.state.price) < 0.75) {
      errors.price = 'Minimum price is $0.75';
    }
    if (Object.keys(errors).length > 0) {
      this.setState({ errors: errors });
      return;
    }
    this.setState({ errors: {}, step: 3 });
  }

  getImageDimensions(image, obj) {
    return new Promise(function (resolve) {
      var fr = new FileReader();
      fr.onload = function () {
        var img = new Image();
        img.onload = function () {
          obj.width = img.width;
          resolve(obj);
        };
        img.src = fr.result;
      };
      fr.readAsDataURL(image);
    });
  }

  compressionWidth(width) {
    if (width > constants.MAX_IMAGE_WIDTH) return constants.MAX_IMAGE_WIDTH;
    if (width >= constants.MIN_IMAGE_WIDTH) return Math.round(width * 0.8);
    return width;
  }

  async addPhoto(event) {
    var inputEl = event.target;
    var file = inputEl.files[0];
    if (!file || this.state.photos.length >= 4) return;

    if (ACCEPTED_TYPES.indexOf(file.type) === -1) {
      this.setState({ photoError: 'Unsupported format. Use JPG, PNG, GIF, or WebP.' });
      inputEl.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      this.setState({ photoError: 'File too large. Max 10 MB.' });
      inputEl.value = '';
      return;
    }

    this.setState({ photoError: '', processingPhoto: true });
    var tmp = {};
    tmp = await this.getImageDimensions(file, tmp);
    var self = this;

    new Compressor(file, {
      quality: constants.IMAGE_COMPRESSION,
      maxWidth: this.compressionWidth(tmp.width),
      success(result) {
        var finalFile = result instanceof File
          ? result
          : new File([result], result.name, { type: result.type, lastModified: Date.now() });

        var reader = new FileReader();
        reader.onload = function (e) {
          self.setState(function (prev) {
            return {
              photos: prev.photos.concat([{ file: finalFile, preview: e.target.result }]),
              processingPhoto: false,
            };
          });
        };
        reader.readAsDataURL(finalFile);
      },
      error: function () {
        self.setState({ photoError: 'Could not process image. Try a JPG or PNG instead.', processingPhoto: false });
      },
    });
    inputEl.value = '';
  }

  removePhoto(index) {
    this.setState(function (prev) {
      return { photos: prev.photos.filter(function (_, i) { return i !== index; }) };
    });
  }

  handlePriceChange(e) {
    var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      this.setState({ price: e.target.value });
    }
  }

  blurPrice() {
    if (this.state.price) {
      this.setState({ price: Number(this.state.price).toFixed(2) });
    }
  }

  handleSubmit() {
    this.setState({ loadingIcon: 'inline', errors: {} });

    var title = this.state.title;
    var description = this.state.description;
    var price = this.state.price;
    var selectedCard = this.state.selectedCard;
    var cardImage = this.state.cardImage;
    var photos = this.state.photos;
    var isSealed = this.state.productType === 'sealed';

    var data = new FormData();
    data.append('title', title);
    data.append('text', description || '');
    data.append('price', Number(price));
    data.append('quantity', Number(this.state.quantity) || 1);
    data.append('allowSharing', this.state.allowSharing);
    data.append('shareCommission', Math.max(1, Number(this.state.shareCommission) || 1));
    data.append('shareCommissionType', 'amount');
    data.append('acceptOffers', this.state.acceptOffers);

    if (isSealed) {
      data.append('productType', 'sealed');
      data.append('category', 'sealed');
      data.append('shippingCost', 0);
      if (this.state.selectedSealedProduct) {
        data.append('cards', this.state.selectedSealedProduct._id);
      }
      if (cardImage) {
        data.append('cardImage', cardImage);
      }
    } else {
      var condition = this.state.condition;
      var category = this.state.category;
      data.append('category', category + ' ' + condition);
      data.append('shippingCost', 0);
      data.append('condition', condition);
      data.append('foil', condition.endsWith('F'));
      if (selectedCard) {
        data.append('cards', selectedCard._id);
        if (selectedCard.rarity) {
          data.append('rarity', selectedCard.rarity);
        }
      }
      if (cardImage) {
        data.append('cardImage', cardImage);
      }
    }
    photos.forEach(function (p) {
      data.append('images', p.file, p.file.name);
    });

    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/posts', {
      mode: 'cors',
      method: 'POST',
      body: data,
    }).then(function (res) {
      mixpanel.track('Product Post', { distinct_id: Auth.getProfile()._id });
      self.setState({ showSuccessModal: true, createdPostId: res._id, loadingIcon: 'none' });
    }).catch(function (err) {
      console.log(err);
      self.setState({ loadingIcon: 'none' });
    });
  }

  isNarutoGame() {
    var game = this.context && this.context.currentGame;
    if (!game) return false;
    return /naruto/i.test((game.name || '') + ' ' + (game.slug || ''));
  }

  renderStepIndicator() {
    var step = this.state.step;
    var isSealed = this.state.productType === 'sealed';
    var steps = isSealed ? ['Product', 'Details', 'Review'] : ['Find Card', 'Details', 'Review'];
    var items = [];
    var self = this;

    for (var i = 0; i < steps.length; i++) {
      var num = i + 1;
      var active = num === step;
      var done = num < step;

      if (i > 0) {
        items.push(
          <div
            key={'line-' + i}
            className="tw-h-0.5 tw-flex-1"
            style={{ backgroundColor: done ? '#c9a84c' : '#e0e0e0', maxWidth: '48px' }}
          />
        );
      }

      items.push(
        <div
          key={num}
          className={'tw-flex tw-flex-col tw-items-center tw-gap-1' + (done ? ' tw-cursor-pointer' : '')}
          onClick={done ? (function (s) { return function () { self.setState({ step: s }); }; })(num) : undefined}
        >
          <div
            className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold"
            style={
              active ? { backgroundColor: '#c9a84c', color: '#000', border: '2px solid #c9a84c' } :
              done ? { backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '2px solid #c9a84c' } :
              { backgroundColor: 'transparent', color: '#999', border: '2px solid #e0e0e0' }
            }
          >
            {done ? '\u2713' : num}
          </div>
          <span
            className={'tw-text-[10px] tw-font-medium' + (done ? ' hover:tw-underline' : '')}
            style={{ color: active || done ? '#c9a84c' : '#999' }}
          >
            {steps[i]}
          </span>
        </div>
      );
    }

    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-8">
        {items}
      </div>
    );
  }

  renderStep1() {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var self = this;
    var productType = this.state.productType;

    var toggleBtnBase = 'tw-flex-1 tw-py-2.5 tw-px-4 tw-text-sm tw-font-semibold tw-rounded-lg tw-border-0 tw-cursor-pointer tw-transition-colors';
    var activeStyle = { backgroundColor: '#c9a84c', color: '#fff' };
    var inactiveStyle = { backgroundColor: isDark ? '#222' : '#f3f4f6', color: isDark ? '#aaa' : '#666' };

    return (
      <div>
        <div className="tw-text-center tw-mb-6">
          <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            What are you selling?
          </h2>
          <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
            Choose your product type
          </p>
        </div>

        {/* Product type toggle */}
        <div className="tw-flex tw-gap-2 tw-mb-6 tw-p-1 tw-rounded-xl" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f0f0f0' }}>
          <button
            className={toggleBtnBase}
            style={productType === 'single' ? activeStyle : inactiveStyle}
            onClick={function () { self.setState({ productType: 'single' }); }}
          >
            <i className="fa fa-clone tw-mr-1.5" />Single Card
          </button>
          <button
            className={toggleBtnBase}
            style={productType === 'sealed' ? activeStyle : inactiveStyle}
            onClick={function () {
              self.setState({ productType: 'sealed' });
            }}
          >
            <i className="fa fa-cube tw-mr-1.5" />Sealed Product
          </button>
        </div>

        {productType === 'single' ? (
          <CardIdentify onCardSelected={(card) => this.onCardSelected(card)} />
        ) : (
          <CardIdentify productType="sealed" onCardSelected={function(card) { self.onSealedCatalogSelected(card); }} />
        )}
      </div>
    );
  }

  renderStep2() {
    var selectedCard = this.state.selectedCard;
    var cardImage = this.state.cardImage;
    var title = this.state.title;
    var description = this.state.description;
    var price = this.state.price;
    var condition = this.state.condition;
    var errors = this.state.errors;
    var cardRarity = selectedCard ? selectedCard.rarity : null;
    var conditionsList = buildConditionsList(null, LEGACY_NO_FOIL_RARITIES, cardRarity);

    var isDark = document.documentElement.classList.contains('tw-dark');
    var inputStyle = {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      borderRadius: '8px',
      border: isDark ? '1px solid #555' : '1px solid #e0e0e0',
      outline: 'none',
      backgroundColor: isDark ? '#141414' : '#fff',
      color: isDark ? '#fff' : '#1a1a1a',
    };

    var self = this;

    return (
      <div>
        <button
          type="button"
          onClick={function () { self.setState({ step: 1, selectedCard: null, cardImage: null }); }}
          className="tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-mb-5 tw-p-0"
          style={{ color: '#999', transition: 'color 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={function (e) { e.currentTarget.style.color = '#999'; }}
        >
          &larr; {self.state.productType === 'sealed' ? 'Change product' : 'Change card'}
        </button>

        {/* Sealed product header */}
        {self.state.productType === 'sealed' && self.state.selectedSealedProduct && (
          <div
            className="tw-flex tw-items-center tw-gap-4 tw-mb-6 tw-p-4 tw-rounded-xl"
            style={{ backgroundColor: isDark ? '#141414' : '#f9fafb', border: isDark ? '2px solid #333' : '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
          >
            {cardImage ? (
              <img src={cardImage} alt={self.state.selectedSealedProduct.name} className="tw-w-16 tw-rounded" style={{ aspectRatio: '3/4', objectFit: 'cover' }} />
            ) : (
              <div className="tw-w-16 tw-h-16 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" style={{ backgroundColor: '#7c3aed' }}>
                <i className="fa fa-cube tw-text-white tw-text-xl" />
              </div>
            )}
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-sm tw-font-bold tw-mb-0.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                {title}
              </p>
              <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
                Sealed Product{self.state.selectedSealedProduct.cardSet ? ' \u2022 ' + self.state.selectedSealedProduct.cardSet : ''}
              </p>
            </div>
          </div>
        )}

        {/* Selected card preview */}
        {cardImage && self.state.productType !== 'sealed' && (
          <div
            className="tw-flex tw-items-center tw-gap-4 tw-mb-6 tw-p-4 tw-rounded-xl"
            style={{ backgroundColor: isDark ? '#141414' : '#f9fafb', border: isDark ? '2px solid #333' : '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
          >
            <img src={cardImage} alt={selectedCard.name} className="tw-w-16 tw-rounded" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-sm tw-font-bold tw-mb-0.5 tw-truncate" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                {selectedCard.name}
                {selectedCard.variant ? ' (' + selectedCard.variant + ')' : ''}
              </p>
              <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#6b7280' : '#999' }}>
                {selectedCard.cardSet}
                {selectedCard.rarity ? ' \u2022 ' + selectedCard.rarity : ''}
              </p>
              {selectedCard.lowestPrice > 0 && (
                <p className="tw-text-xs tw-font-semibold tw-mb-0 tw-mt-1" style={{ color: '#c9a84c' }}>
                  Market: ${Number(selectedCard.lowestPrice).toFixed(2)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pricing Guide (singles only) */}
        {this.state.selectedCard && this.state.productType !== 'sealed' && this.renderPricingWidget()}

        {/* Price + Quantity side-by-side */}
        <div className="tw-flex tw-gap-3 tw-mb-4">
          <div className="tw-flex-1">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Price</label>
            {(function () {
              var priceValid = price !== '' && Number(price) >= 0.75;
              var priceTouched = self.state.priceTouched;
              var borderColor = priceTouched ? (priceValid ? '#22c55e' : '#ef4444') : (isDark ? '#555' : '#e0e0e0');
              return (
                <div style={{ position: 'relative' }}>
                  <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)', zIndex: 1 }}>$</span>
                  <input
                    type="text"
                    value={price}
                    onChange={function (e) { self.handlePriceChange(e); }}
                    onBlur={function () { self.setState({ priceTouched: true }); self.blurPrice(); }}
                    placeholder="0.00"
                    style={Object.assign({}, inputStyle, { paddingLeft: '24px', paddingRight: '32px', borderColor: borderColor })}
                    className="focus:tw-border-brand-gold"
                  />
                  {priceTouched && (
                    <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: priceValid ? '#22c55e' : '#ef4444', pointerEvents: 'none' }}>
                      {priceValid ? '\u2713' : '\u2717'}
                    </span>
                  )}
                </div>
              );
            })()}
            {errors.price && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{errors.price}</p>}
          </div>
          <div style={{ width: '100px' }}>
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Qty</label>
            <input
              type="number"
              min="1"
              value={self.state.quantity}
              onChange={function (e) {
                var val = e.target.value;
                if (val === '' || (Number(val) >= 1 && Number(val) <= 999)) {
                  self.setState({ quantity: val });
                }
              }}
              style={inputStyle}
              className="focus:tw-border-brand-gold"
            />
          </div>
        </div>
        <p className="tw-text-xs tw-text-[#999] tw-mt-0 tw-mb-2">
          <i className="fa fa-truck tw-mr-1" style={{ color: '#c9a84c' }}></i>
          Shipping: $1.00 flat rate (free on orders $10+). Tracking required at $50+.
        </p>
        {(function () {
          var p = Number(price);
          if (!p || p < 0.75) return null;
          var shipping = p >= 10 ? 0 : 1.00;
          var sellerAmt = (p * 0.90) + shipping;
          var payment = p + shipping + 0.50;
          var net = payment - sellerAmt;
          var procFee = (payment * 0.029) + 0.30;
          var due = net - procFee;
          if (due < 0.75) {
            var shortfall = due <= 0 ? 0.75 : 0.75 - due;
            sellerAmt = sellerAmt - shortfall;
          }
          var payout = Math.floor(sellerAmt * 100) / 100;
          var feeAmt = Math.round((p + shipping - payout) * 100) / 100;
          return (
            <div className="tw-rounded-lg tw-p-2.5 tw-mb-4" style={{ backgroundColor: isDark ? 'rgba(22,163,106,0.08)' : 'rgba(22,163,106,0.05)', border: '1px solid ' + (isDark ? 'rgba(22,163,106,0.2)' : 'rgba(22,163,106,0.15)') }}>
              <div className="tw-flex tw-justify-between tw-items-center">
                <span className="tw-text-xs" style={{ color: isDark ? '#aaa' : '#666' }}>
                  <i className="fa fa-info-circle tw-mr-1" style={{ color: '#c9a84c' }}></i>
                  Estimated payout each
                </span>
                <span className="tw-text-sm tw-font-bold" style={{ color: '#16a34a' }}>${payout.toFixed(2)}</span>
              </div>
              <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                Platform fee: ${feeAmt.toFixed(2)}{shipping > 0 ? ' \u00b7 Shipping passed to you in full' : ''}
              </p>
              {feeAmt / p > 0.11 ? (
                <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: isDark ? '#777' : '#999' }}>
                  Fee is lower when buyers purchase multiple cards from you. <a href="/faq#payout-fees" target="_blank" rel="noopener noreferrer" className="tw-no-underline" style={{ color: '#c9a84c' }}>Learn more</a>
                </p>
              ) : null}
            </div>
          );
        })()}

        {/* Condition (singles only) */}
        {self.state.productType !== 'sealed' && <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-semibold tw-mb-1.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Condition/Foil</label>
          <select
            value={condition}
            onChange={function (e) { self.setState({ condition: e.target.value }); }}
            style={inputStyle}
            className="focus:tw-border-brand-gold"
          >
            {(function () {
              var nonFoil = conditionsList.filter(function (c) { return !c.foilOnly; });
              var foil = conditionsList.filter(function (c) { return c.foilOnly; });
              if (foil.length > 0) {
                return [
                  <optgroup key="nf" label="Non-Foil">
                    {nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                  </optgroup>,
                  <optgroup key="f" label="Foil">
                    {foil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
                  </optgroup>
                ];
              }
              return nonFoil.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; });
            })()}
          </select>
        </div>}

        {/* Add Card Details — collapsible */}
        <div className="tw-mb-4">
          <button
            type="button"
            onClick={function () { self.setState({ showCardDetails: !self.state.showCardDetails }); }}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-font-semibold hover:tw-underline"
            style={{ color: '#c9a84c' }}
          >
            <i className={'fa ' + (self.state.showCardDetails ? 'fa-chevron-down' : 'fa-pencil')} style={{ fontSize: '12px' }} />
            {self.state.showCardDetails ? 'Hide card details' : 'Add card details'}
            <span className="tw-text-xs tw-font-normal tw-text-[#999]">(optional)</span>
          </button>
          {self.state.showCardDetails && (
            <div className="tw-mt-3">
              <div className="tw-mb-3">
                <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Title</label>
                {(function () {
                  var titleValid = title.trim().length > 0;
                  var titleTouched = self.state.titleTouched;
                  var borderColor = titleTouched ? (titleValid ? '#22c55e' : '#ef4444') : (isDark ? '#555' : '#e0e0e0');
                  return (
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={title}
                        onChange={function (e) { self.setState({ title: e.target.value }); }}
                        onBlur={function () { self.setState({ titleTouched: true }); }}
                        style={Object.assign({}, inputStyle, { paddingRight: '32px', borderColor: borderColor })}
                        className="focus:tw-border-brand-gold"
                      />
                      {titleTouched && (
                        <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: titleValid ? '#22c55e' : '#ef4444', pointerEvents: 'none' }}>
                          {titleValid ? '\u2713' : '\u2717'}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {errors.title && <p className="tw-text-red-500 tw-text-xs tw-mt-1 tw-mb-0">{errors.title}</p>}
              </div>
              <div>
                <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Description</label>
                <textarea
                  value={description}
                  onChange={function (e) { self.setState({ description: e.target.value }); }}
                  rows={3}
                  maxLength={500}
                  style={Object.assign({}, inputStyle, { resize: 'none' })}
                  className="focus:tw-border-brand-gold"
                />
                <p style={{ fontSize: '12px', textAlign: 'right', margin: '4px 0 0', color: description.length >= 500 ? '#ef4444' : description.length >= 450 ? '#c9a84c' : '#999' }}>{description.length} / 500</p>
              </div>
            </div>
          )}
        </div>

        {/* Share & Earn Toggle + Commission */}
        {(function () {
          var priceTooLow = !self.state.price || Number(self.state.price) < SHARE_MIN_PRICE;
          var sharingEnabled = self.state.allowSharing && !priceTooLow;
          return (
        <div className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Share & Earn</label>
            <button
              type="button"
              onClick={function () { if (!priceTooLow) { self.setState({ allowSharing: !self.state.allowSharing }); } }}
              className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-transition-colors tw-flex-shrink-0"
              style={{ backgroundColor: sharingEnabled ? '#c9a84c' : '#ccc', cursor: priceTooLow ? 'not-allowed' : 'pointer', opacity: priceTooLow ? 0.5 : 1 }}
            >
              <span
                className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                style={{ left: sharingEnabled ? '22px' : '2px' }}
              />
            </button>
          </div>
          {priceTooLow ? (
            <p className="tw-text-xs tw-mt-0 tw-mb-2" style={{ color: '#c9a84c' }}>Share & Earn is available for items $10.00 and above</p>
          ) : (
            <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-2">Let others promote your listing and earn a commission on sales</p>
          )}
          {sharingEnabled && (
            <div>
              <label className="tw-block tw-text-xs tw-font-medium tw-mb-1" style={{ color: isDark ? '#aaa' : '#555' }}>Commission Amount</label>
              <div className="tw-relative">
                <span className="tw-absolute tw-left-3 tw-top-1/2 tw-text-sm tw-text-[#999] tw-font-medium" style={{ transform: 'translateY(-50%)' }}>$</span>
                <input
                  type="text"
                  value={self.state.shareCommission}
                  onChange={function (e) {
                    var val = e.target.value;
                    var re = /^(?!\.?$)\d{0,4}(\.\d{0,2})?$/;
                    if (val === '' || re.test(val)) {
                      var maxComm = Math.floor(Number(self.state.price || 0) * 0.90 * 100) / 100;
                      var numVal = Number(val);
                      if (val !== '' && numVal > maxComm) { val = String(maxComm); numVal = maxComm; }
                      self.setState({ shareCommission: val === '' ? '' : numVal || val });
                    }
                  }}
                  onBlur={function () {
                    var num = Number(self.state.shareCommission);
                    if (!num || num < 1) { self.setState({ shareCommission: 1 }); }
                  }}
                  placeholder="1.00"
                  style={Object.assign({}, inputStyle, { paddingLeft: '24px' })}
                  className="focus:tw-border-brand-gold"
                />
              </div>
              {self.state.price ? (
                <p className="tw-text-xs tw-mt-1 tw-mb-0" style={{ color: '#c9a84c' }}>Marketer earns ${Math.max(1, Number(self.state.shareCommission) || 1).toFixed(2)} per sale (minimum $1.00)</p>
              ) : null}
              {self.state.price ? (
                <p className="tw-text-xs tw-text-[#888] tw-mt-0.5 tw-mb-0">Max: ${(Math.floor(Number(self.state.price) * 0.90 * 100) / 100).toFixed(2)} (your 90% earnings)</p>
              ) : null}
              <p className="tw-text-xs tw-text-[#888] tw-mt-1 tw-mb-0">Deducted from your earnings when someone buys through a share link</p>
            </div>
          )}
        </div>
          );
        })()}

        {/* Open to Offers Toggle */}
        <div className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
            <label className="tw-block tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Open to Offers</label>
            <button
              type="button"
              onClick={function () { self.setState({ acceptOffers: !self.state.acceptOffers }); }}
              className="tw-relative tw-w-10 tw-h-5 tw-rounded-full tw-border-none tw-cursor-pointer tw-transition-colors tw-flex-shrink-0"
              style={{ backgroundColor: self.state.acceptOffers ? '#c9a84c' : '#ccc' }}
            >
              <span
                className="tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-transition-all tw-shadow-sm"
                style={{ left: self.state.acceptOffers ? '22px' : '2px' }}
              />
            </button>
          </div>
          <p className="tw-text-xs tw-text-[#888] tw-mt-0 tw-mb-0">Allow buyers to make offers below your listed price</p>
        </div>

        {/* Naruto CCG photo disclaimer */}
        {self.isNarutoGame() && (
          <div
            className="tw-mb-4 tw-p-3 tw-rounded-lg tw-flex tw-items-start tw-gap-2.5"
            style={{
              backgroundColor: 'rgba(201,168,76,0.1)',
              border: '1px solid rgba(201,168,76,0.35)',
            }}
          >
            <i className="fa fa-camera tw-mt-0.5" style={{ color: '#c9a84c', fontSize: '14px' }} />
            <p className="tw-text-xs tw-mb-0 tw-mt-0" style={{ color: isDark ? '#d1d5db' : '#555', lineHeight: 1.6 }}>
              <span className="tw-font-bold" style={{ color: '#c9a84c' }}>Naruto CCG sellers:</span>{' '}
              the best sales results come from posting photographs of the actual card you are selling.
              Listings with real photos build buyer trust and sell far better than stock images alone.
            </p>
          </div>
        )}

        {/* Add Photos — collapsible */}
        <div className="tw-mb-4">
          <button
            type="button"
            onClick={function () { self.setState({ showPhotos: !self.state.showPhotos }); }}
            className="tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-sm tw-font-semibold hover:tw-underline"
            style={{ color: '#c9a84c' }}
          >
            <i className={'fa ' + (self.state.showPhotos ? 'fa-chevron-down' : 'fa-camera')} style={{ fontSize: '12px' }} />
            {self.state.showPhotos ? 'Hide photos' : 'Add photos'}
            <span className="tw-text-xs tw-font-normal tw-text-[#999]">(optional)</span>
            {!self.state.showPhotos && self.state.photos.length > 0 && (
              <span
                className="tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-full tw-ml-1"
                style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}
              >
                {self.state.photos.length}
              </span>
            )}
          </button>
          {self.state.showPhotos && (
            <div className="tw-mt-3">
              <p className="tw-text-xs tw-mb-3 tw-mt-0" style={{ color: isDark ? '#4b5563' : '#bbb' }}>
                Up to 4 photos &middot; JPG, PNG, GIF, or WebP &middot; 10 MB max
              </p>
              {self.state.photoError && (
                <p className="tw-text-red-500 tw-text-sm tw-mb-3 tw-mt-0">{self.state.photoError}</p>
              )}
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                {self.state.photos.map(function (photo, i) {
                  return (
                    <div key={i} className="tw-relative tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                      <img src={photo.preview} alt={'Photo ' + (i + 1)} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                      <button
                        type="button"
                        onClick={function () { self.removePhoto(i); }}
                        className="tw-absolute tw-top-2 tw-right-2 tw-w-7 tw-h-7 tw-rounded-full tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-justify-center"
                        style={{ lineHeight: 1 }}
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
                {self.state.processingPhoto && (
                  <div className="tw-aspect-square tw-rounded-xl tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                      <div className="tw-w-8 tw-h-8 tw-rounded-full tw-border-2 tw-border-[#e0e0e0]" style={{ borderTopColor: '#c9a84c', animation: 'spin 0.8s linear infinite' }} />
                      <span className="tw-text-[10px] tw-text-[#999]">Processing</span>
                    </div>
                  </div>
                )}
                {!self.state.processingPhoto && self.state.photos.length < 4 && (
                  <label
                    className="tw-aspect-square tw-rounded-xl tw-border-2 tw-border-dashed tw-flex tw-flex-col tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"
                    style={{ borderColor: isDark ? '#444' : '#d0d0d0', backgroundColor: isDark ? '#0f0f0f' : '#f9fafb' }}
                    onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                    onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#444' : '#d0d0d0'; }}
                  >
                    <i className="fa fa-camera tw-text-2xl tw-text-[#bbb] tw-mb-1" />
                    <span className="tw-text-xs tw-text-[#999] tw-font-medium">Add Photo</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      onChange={function (e) { self.addPhoto(e); }}
                      style={{ display: 'none' }}
                    />
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={function () { self.goToReview(); }}
          className="tw-w-full tw-font-bold tw-py-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none"
          style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
          onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          Continue
        </button>
      </div>
    );
  }

  renderStep3() {
    var selectedCard = this.state.selectedCard;
    var cardImage = this.state.cardImage;
    var title = this.state.title;
    var description = this.state.description;
    var price = this.state.price;
    var condition = this.state.condition;
    var photos = this.state.photos;
    var errors = this.state.errors;
    var loadingIcon = this.state.loadingIcon;
    var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
    var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;
    var isDark = document.documentElement.classList.contains('tw-dark');

    var condLabel = LEGACY_CONDITIONS.find(function (c) { return c.value === condition; });
    var self = this;

    return (
      <div>
        <button
          type="button"
          onClick={function () { self.setState({ step: 2 }); }}
          className="tw-text-sm tw-bg-transparent tw-border-none tw-cursor-pointer tw-mb-5 tw-p-0"
          style={{ color: '#999', transition: 'color 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.color = '#c9a84c'; }}
          onMouseLeave={function (e) { e.currentTarget.style.color = '#999'; }}
        >
          &larr; Back to details
        </button>

        <div className="tw-text-center tw-mb-6">
          <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
            Review Listing
          </h2>
        </div>

        {/* Preview card */}
        <div
          className="tw-rounded-xl tw-p-5 tw-mb-5"
          style={{ backgroundColor: isDark ? '#141414' : '#f9fafb', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)' }}
        >
          <div className="tw-flex tw-gap-4">
            {cardImage && (
              <img src={cardImage} alt={title} className="tw-w-20 tw-rounded tw-flex-shrink-0" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
            )}
            <div className="tw-flex-1 tw-min-w-0">
              <h3 className="tw-text-base tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>{title}</h3>
              {condLabel && (
                <span
                  className="tw-inline-block tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-mb-2"
                  style={{ backgroundColor: 'rgba(201,168,76,0.12)', color: '#c9a84c' }}
                >
                  {condLabel.label}
                </span>
              )}
              <p className="tw-text-2xl tw-font-bold tw-mb-0.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                ${Number(price).toFixed(2)}
              </p>
              <p className="tw-text-xs tw-text-[#999] tw-mb-0">
                {Number(price) >= FREE_SHIPPING_THRESHOLD ? 'Free Shipping' : '+ $' + FLAT_SHIPPING_FEE.toFixed(2) + ' shipping'}
              </p>
            </div>
          </div>
          {description && (
            <p className="tw-text-sm tw-mt-4 tw-mb-0" style={{ color: isDark ? '#9ca3af' : '#666' }}>{description}</p>
          )}
        </div>

        {/* Condition photos */}
        {photos.length > 0 && (
          <div className="tw-mb-5">
            <p className="tw-text-xs tw-font-semibold tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-2">Condition Photos</p>
            <div className="tw-flex tw-gap-2">
              {photos.map(function (photo, i) {
                return (
                  <div key={i} className="tw-w-16 tw-h-16 tw-rounded-lg tw-overflow-hidden tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: isDark ? '#1a1a1a' : '#f3f4f6', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                    <img src={photo.preview} alt={'Photo ' + (i + 1)} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Shipping info */}
        <div className="tw-mb-6">
          <div className="tw-rounded-lg tw-p-3" style={{ backgroundColor: isDark ? '#141414' : '#f9f9f9', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
            <p className="tw-text-sm tw-mb-0" style={{ color: isDark ? '#aaa' : '#555' }}>
              <i className="fa fa-truck tw-mr-1.5" style={{ color: '#c9a84c' }}></i>
              {Number(price) >= FREE_SHIPPING_THRESHOLD
                ? 'Free shipping. You must provide tracking when shipping this order.'
                : 'Buyer pays $' + FLAT_SHIPPING_FEE.toFixed(2) + ' flat shipping. Tracking is optional \u2014 buyers can add it at checkout for +$5.50.'}
            </p>
            <p className="tw-text-xs tw-mt-2 tw-mb-0" style={{ color: isDark ? '#777' : '#888' }}>
              <Link
                to="/shipping"
                className="tw-no-underline"
                style={{ color: '#c9a84c', transition: 'color 0.15s ease' }}
                onMouseEnter={function (e) { e.currentTarget.style.color = '#b8953f'; }}
                onMouseLeave={function (e) { e.currentTarget.style.color = '#c9a84c'; }}
              >View Shipping Guide</Link>
            </p>
          </div>
        </div>

        {/* Payout setup banner */}
        {self.state.hasPayoutMethod === false && (
          <a href="/settings?tab=payments" target="_blank" rel="noopener noreferrer" className="tw-block tw-mb-5 tw-p-4 tw-rounded-xl hover:tw-opacity-90 tw-transition-opacity" style={{ backgroundColor: 'rgba(196,163,90,0.08)', border: '1px solid rgba(196,163,90,0.25)' }}>
            <div className="tw-flex tw-items-start tw-gap-3">
              <i className="fa fa-info-circle tw-text-lg" style={{ lineHeight: '20px', color: '#c4a35a' }} />
              <div>
                <p className="tw-text-sm tw-font-semibold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>Set up payouts to get paid</p>
                <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#aaa' : '#666' }}>
                  You can list items now! Connect Stripe or PayPal in your payment settings to receive earnings when your items sell.
                </p>
              </div>
              <i className="fa fa-external-link tw-text-xs tw-flex-shrink-0" style={{ color: '#c4a35a', marginTop: '3px' }} />
            </div>
          </a>
        )}

        <button
          type="button"
          onClick={function () { self.handleSubmit(); }}
          disabled={loadingIcon === 'inline'}
          className="tw-w-full tw-font-bold tw-py-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
          onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
          onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {loadingIcon === 'inline' ? 'Creating Listing...' : 'Create Listing'}
        </button>
      </div>
    );
  }

  render() {
    var step = this.state.step;
    var isDark = document.documentElement.classList.contains('tw-dark');

    return (
      <div className="tw-min-h-[calc(100vh-64px)]" style={{ backgroundColor: isDark ? '#111' : '#fff' }}>
        <Helmet>
          <title>Sell | Primal Marketplace</title>
        </Helmet>

        {/* Page header */}
        <div className="tw-max-w-lg tw-mx-auto tw-px-4 tw-pt-8 tw-pb-2">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
            <h1 className="tw-text-2xl tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
              {this.state.productType === 'sealed' ? 'Sell a Product' : 'Sell a Card'}
            </h1>
            <Link
              to="/sell/bulk"
              className="tw-text-sm tw-font-medium tw-no-underline tw-px-3 tw-py-1.5 tw-rounded-lg tw-transition-colors"
              style={{ color: '#c9a84c', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
              onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
              onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
            >
              <i className="fa fa-th-list tw-mr-1" />
              Bulk Sell
            </Link>
          </div>

          {this.renderStepIndicator()}

          {step === 1 && this.renderStep1()}
          {step === 2 && this.renderStep2()}
          {step === 3 && this.renderStep3()}

          {/* Bottom padding */}
          <div className="tw-h-12" />
        </div>

        {/* Success Modal */}
        {this.state.showSuccessModal && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-50"
              style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
              onClick={function() { this.setState({ showSuccessModal: false }); }.bind(this)}
            />
            <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4 tw-pointer-events-none">
              <div
                className="modal-enter tw-rounded-2xl tw-shadow-xl tw-w-full tw-max-w-sm tw-p-6 tw-text-center tw-pointer-events-auto"
                style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}
              >

                {/* Checkmark */}
                <div className="tw-flex tw-justify-center tw-mb-4">
                  <div className="tw-w-16 tw-h-16 tw-rounded-full tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: '#22c55e' }}>
                    <i className="fa fa-check tw-text-white tw-text-2xl" />
                  </div>
                </div>

                <h2 className="tw-text-xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                  Listing Created!
                </h2>
                <p className="tw-text-sm tw-mb-5" style={{ color: isDark ? '#9ca3af' : '#666' }}>
                  Your {this.state.productType === 'sealed' ? 'product' : 'card'} is now live on the marketplace
                </p>

                {/* Card preview */}
                <div className="tw-rounded-lg tw-p-3 tw-mb-5 tw-flex tw-items-center tw-gap-3 tw-text-left" style={{ backgroundColor: isDark ? '#141414' : '#f9f9f9', border: isDark ? '1px solid #333' : '1px solid #e0e0e0' }}>
                  {this.state.cardImage && (
                    <img src={this.state.cardImage} alt={this.state.title} className="tw-w-14 tw-rounded tw-flex-shrink-0" style={{ aspectRatio: '63/88', objectFit: 'contain' }} />
                  )}
                  <div className="tw-flex-1 tw-min-w-0">
                    <p className="tw-text-sm tw-font-semibold tw-truncate tw-mb-0.5" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>{this.state.title}</p>
                    <p className="tw-text-lg tw-font-bold tw-mb-0" style={{ color: '#c9a84c' }}>${Number(this.state.price).toFixed(2)}</p>
                  </div>
                </div>

                {/* Payout reminder */}
                {this.state.hasPayoutMethod === false && (
                  <div className="tw-rounded-lg tw-p-3 tw-mb-5 tw-text-left" style={{ backgroundColor: 'rgba(196,163,90,0.08)', border: '1px solid rgba(196,163,90,0.25)' }}>
                    <p className="tw-text-sm tw-font-semibold tw-mb-1" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                      <i className="fa fa-info-circle tw-mr-1.5" style={{ color: '#c4a35a' }} />
                      Set up payouts to get paid
                    </p>
                    <p className="tw-text-xs tw-mb-0" style={{ color: isDark ? '#aaa' : '#666' }}>
                      Connect Stripe or add your PayPal email so you can receive earnings when your items sell.
                    </p>
                  </div>
                )}

                {/* Action buttons */}
                <div className="tw-flex tw-flex-col tw-gap-2.5">
                  <button
                    type="button"
                    onClick={function() { this.props.history.push('/product/' + this.state.createdPostId); }.bind(this)}
                    className="tw-w-full tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none"
                    style={{ backgroundColor: '#c9a84c', color: '#000', transition: 'all 0.15s ease' }}
                    onMouseEnter={function (e) { e.currentTarget.style.backgroundColor = '#b8953f'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(201,168,76,0.3)'; }}
                    onMouseLeave={function (e) { e.currentTarget.style.backgroundColor = '#c9a84c'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    View Listing
                  </button>
                  {this.state.hasPayoutMethod === false && (
                    <a
                      href="/settings?tab=payments"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-block tw-w-full tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-text-center tw-transition-colors"
                      style={{ color: '#c9a84c', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', transition: 'border-color 0.15s ease' }}
                      onMouseEnter={function (e) { e.currentTarget.style.borderColor = '#c9a84c'; }}
                      onMouseLeave={function (e) { e.currentTarget.style.borderColor = isDark ? '#333' : '#e0e0e0'; }}
                    >
                      Set Up Payouts
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={function() { this.setState({ showSuccessModal: false, createdPostId: null, step: 1, selectedCard: null, cardImage: null, title: '', description: '', price: '', quantity: '1', condition: 'NM', photos: [], showPhotos: false, showCardDetails: false, allowSharing: false, acceptOffers: false }); }.bind(this)}
                    className="tw-w-full tw-py-2 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none tw-bg-transparent tw-transition-colors"
                    style={{ color: '#999' }}
                  >
                    {this.state.productType === 'sealed' ? 'List Another Product' : 'List Another Card'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default withAuth(Post);
